import type { Table as TableInstance } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";

interface ListPaginationProps {
  total: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (next: number) => void;
}

/**
 * Shared pagination footer. Always shows the row-count summary so filter math
 * is verifiable; prev/next controls collapse when the list fits on one page.
 */
export function ListPagination({
  total,
  pageIndex,
  pageSize,
  onPageChange,
}: ListPaginationProps): ReactNode {
  const totalPages = Math.max(Math.ceil(total / Math.max(pageSize, 1)), 1);
  const start = total === 0 ? 0 : pageIndex * pageSize + 1;
  const end = Math.min((pageIndex + 1) * pageSize, total);
  const showControls = total > pageSize;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2">
      <span className="text-2xs text-text-tertiary">
        Showing{" "}
        <span className="font-medium text-text-secondary tabular-nums">
          {start}-{end}
        </span>{" "}
        of <span className="font-medium text-text-secondary tabular-nums">{total}</span>
      </span>
      {showControls ? (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={pageIndex === 0}
            onClick={() => onPageChange(pageIndex - 1)}
          >
            <ChevronLeft className="size-3.5" />
            Previous
          </Button>
          <span className="px-2 font-mono text-2xs text-text-tertiary tabular-nums">
            Page {pageIndex + 1} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={pageIndex + 1 >= totalPages}
            onClick={() => onPageChange(pageIndex + 1)}
          >
            Next
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function TablePagination<TData>({ table }: { table: TableInstance<TData> }): ReactNode {
  const total = table.getFilteredRowModel().rows.length;
  const pageSize = table.getState().pagination.pageSize;
  const pageIndex = table.getState().pagination.pageIndex;
  return (
    <ListPagination
      total={total}
      pageIndex={pageIndex}
      pageSize={pageSize}
      onPageChange={(next) => table.setPageIndex(next)}
    />
  );
}
